import * as React from 'react';
import { UseFormReturn } from 'react-hook-form';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { GroupedCombobox } from '@/components/ui/grouped-combobox';
import { Input } from '@/components/ui/input';
import { INSURANCE_CARRIER_CONTACTS } from '@/constants/insurance-carriers';
import { useGetCoveragesQuery } from '@/services/api/coverageService';
import { useGetNotesQuery } from '@/services/api/matterNotesService';
import { useGetMedicalProvidersQuery } from '@/services/api/treatmentService';
import { ContactHandleSchema } from '@/services/types/coverage-types';
import { Contact, ContactType } from '@/services/types/task-types';
import { ContactHandleType } from '@/services/types/treatment-types';
import { isPhoneNumberValid } from '@/utils/validation';

import {
  InsuranceNote,
  NOTE_TYPES,
} from '../../services/types/matter-notes-types';

// Format phone number for display
const formatPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return '';

  // Handle E.164 format
  if (phoneNumber.startsWith('+')) {
    // Extract country code and number
    const match = phoneNumber.match(/^\+(\d{1})(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `+${match[1]} (${match[2]}) ${match[3]}-${match[4]}`;
    }
  }

  // Handle standard US formats
  const cleaned = phoneNumber.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  // Return as-is if we can't format it
  return phoneNumber;
};

interface ContactSelectionDisplayProps {
  provider: Contact;
  displayValue: string;
  isCustomMode: boolean;
  phoneNumber: string;
  onClear: (e: React.MouseEvent) => void;
}

const ContactSelectionDisplay: React.FC<ContactSelectionDisplayProps> = ({
  provider,
  displayValue,
  isCustomMode,
  phoneNumber,
  onClear,
}) => (
  <div className="flex flex-col gap-1.5 min-w-0 w-full py-1">
    <div className="font-semibold text-sm truncate text-left flex justify-between items-center">
      <span className="truncate text-gray-900">
        {displayValue}
        {isCustomMode && ' (Custom Number)'}
      </span>
      <button
        type="button"
        className="text-gray-500 hover:text-gray-900 cursor-pointer border text-xs font-medium px-2 py-0.5 rounded hover:bg-gray-100 transition-colors ml-2 flex-shrink-0"
        onClick={onClear}
      >
        Clear
      </button>
    </div>
    <div className="text-xs text-left flex flex-col gap-0.5">
      {provider.secondaryTitle && (
        <span className="text-gray-500">{provider.secondaryTitle}</span>
      )}
      <span className="text-gray-700 font-medium">
        {formatPhoneNumber(isCustomMode ? phoneNumber : provider.phone)}
      </span>
    </div>
  </div>
);

const useContacts = (matterId: string) => {
  const { data: medicalProviders } = useGetMedicalProvidersQuery(matterId);
  const { data: coverages } = useGetCoveragesQuery({ matterId });

  const { data: insuranceNotes } = useGetNotesQuery({
    matterId,
    noteType: NOTE_TYPES.INSURANCE,
  });

  const contacts = React.useMemo(() => {
    const noteContacts: Contact[] = [];

    if (medicalProviders) {
      // Add main provider contact
      noteContacts.push(
        ...medicalProviders
          .filter((provider) => provider.phone)
          .map((provider) => ({
            id: provider.id,
            name: provider.name || 'Unnamed Provider',
            phone: provider.phone || '',
            type: ContactType.MEDICAL_PROVIDER,
          })),
      );

      // Add provider phone handles as additional contacts
      medicalProviders.forEach((provider) => {
        if (provider.handles && provider.handles.length > 0) {
          const phoneHandles = provider.handles.filter(
            (handle) =>
              handle.handle_type === ContactHandleType.PHONE &&
              handle.value &&
              handle.value !== provider.phone,
          );

          if (phoneHandles.length > 0) {
            noteContacts.push(
              ...phoneHandles.map((handle, index) => ({
                id: `${provider.id}-handle-${index}`,
                name: provider.name || 'Unnamed Provider',
                secondaryTitle: handle.name || 'Phone',
                phone: handle.value || '',
                type: ContactType.MEDICAL_PROVIDER,
                parentId: provider.id, // Store the original provider ID
              })),
            );
          }
        }
      });
    }

    // Add coverage contacts
    if (coverages) {
      coverages.forEach((coverage) => {
        // Add carrier contacts
        if (coverage.carrier?.handles) {
          const phoneHandles = coverage.carrier.handles.filter(
            (handle: ContactHandleSchema) =>
              handle.handle_type === 'PHONE' &&
              handle.value &&
              isPhoneNumberValid(handle.value),
          );

          phoneHandles.forEach((handle, index) => {
            noteContacts.push({
              id: `coverage-${coverage.id}-carrier-${index}`,
              name: coverage.carrier?.name || 'Unknown Carrier',
              secondaryTitle: handle.name || 'Carrier',
              phone: handle.value,
              type: ContactType.INSURANCE_CARRIER,
              parentId: coverage.id, // Store the coverage ID as parent
            });
          });
        }

        // Add adjuster contacts
        if (
          coverage.adjusters &&
          coverage.adjusters.length > 0 &&
          coverage.adjusters[0]?.handles
        ) {
          const phoneHandles = coverage.adjusters[0].handles.filter(
            (handle: ContactHandleSchema) => handle.handle_type === 'PHONE',
          );

          phoneHandles.forEach((handle: ContactHandleSchema, index: number) => {
            noteContacts.push({
              id: `coverage-adjuster-${coverage.id}-${index}`,
              name: coverage.adjusters?.[0]?.name || 'Unknown Adjuster',
              secondaryTitle: handle.name || 'Adjuster',
              phone: handle.value,
              type: ContactType.INSURANCE_ADJUSTER,
              parentId: coverage.id, // Store the coverage ID as parent
            });
          });
        }

        // Add subrogator contacts
        if (coverage.subrogator?.handles) {
          const phoneHandles = coverage.subrogator.handles.filter(
            (handle: ContactHandleSchema) =>
              handle.handle_type === 'PHONE' &&
              handle.value &&
              isPhoneNumberValid(handle.value),
          );

          phoneHandles.forEach((handle, index) => {
            noteContacts.push({
              id: `coverage-${coverage.id}-subrogator-${index}`,
              name: coverage.subrogator?.name || 'Unknown Subrogator',
              secondaryTitle: handle.name || 'Subrogator',
              phone: handle.value,
              type: ContactType.INSURANCE_ADJUSTER, // Using INSURANCE_ADJUSTER as closest type
              parentId: coverage.id, // Store the coverage ID as parent
            });
          });
        }
      });
    }

    if (insuranceNotes) {
      noteContacts.push(
        ...(insuranceNotes as InsuranceNote[])
          .filter((note) => note.adjuster_phone)
          .map((note) => ({
            id: note.id,
            name: note.carrier || 'Unknown Carrier',
            secondaryTitle: note.adjuster_name || 'Adjuster',
            phone: note.adjuster_phone || '',
            type: ContactType.INSURANCE_ADJUSTER,
          })),
      );
    }

    return noteContacts;
  }, [medicalProviders, coverages, insuranceNotes]);

  return { contacts };
};

interface ContactSelectorProps {
  form: UseFormReturn<any>;
  matterId: string;
  isMedicalTask: boolean;
  isInsuranceTask: boolean;
  allowCustomPhoneNumber?: boolean;
}

export const ContactSelector: React.FC<ContactSelectorProps> = ({
  form,
  matterId,
  isMedicalTask,
  isInsuranceTask,
  allowCustomPhoneNumber = true,
}) => {
  const { contacts } = useContacts(matterId);
  const selectedContact = form.watch('provider');
  const phoneNumber = form.watch('phoneNumber');

  // Determine if we're in custom mode by comparing phone numbers
  // Only allow custom mode if allowCustomPhoneNumber is true
  const isCustomMode =
    allowCustomPhoneNumber &&
    selectedContact &&
    phoneNumber !== selectedContact.phone;

  // Categorize providers into suggested and all
  const categorizedProviders = React.useMemo(() => {
    // Get suggested contacts based on task type
    const suggested = contacts.filter((provider) => {
      if (isMedicalTask && provider.type === ContactType.MEDICAL_PROVIDER)
        return true;
      if (
        isInsuranceTask &&
        (provider.type === ContactType.INSURANCE_ADJUSTER ||
          provider.type === ContactType.INSURANCE_CARRIER)
      )
        return true;
      return false;
    });

    // All other matter contacts (excluding those in suggested)
    const allOthers = contacts.filter(
      (contact) => !suggested.some((s) => s.id === contact.id),
    );

    // Filter insurance carrier contacts to only include those with valid phone numbers
    const validInsuranceCarrierContacts = INSURANCE_CARRIER_CONTACTS.filter(
      (carrier) => carrier.phone && isPhoneNumberValid(carrier.phone),
    );

    return { suggested, allOthers, validInsuranceCarrierContacts };
  }, [contacts, isMedicalTask, isInsuranceTask]);

  // Set first suggested contact as default if available
  React.useEffect(() => {
    const [firstSuggested] = categorizedProviders.suggested;
    // Only set default if there's a suggested contact and no contact or phone number already set
    if (firstSuggested && !selectedContact && !phoneNumber) {
      form.setValue('provider', firstSuggested);
      form.setValue('phoneNumber', firstSuggested.phone);
    }
  }, [categorizedProviders.suggested, form, selectedContact, phoneNumber]);

  // When a contact is selected and custom phone numbers are not allowed,
  // ensure the phone number is always set to the contact's phone
  React.useEffect(() => {
    if (
      !allowCustomPhoneNumber &&
      selectedContact &&
      phoneNumber !== selectedContact.phone
    ) {
      form.setValue('phoneNumber', selectedContact.phone);
    }
  }, [allowCustomPhoneNumber, selectedContact, phoneNumber, form]);

  // Create a display value function that includes secondary title when present
  const getContactDisplayValue = (contact: Contact) => {
    if (contact.secondaryTitle) {
      return `${contact.name} (${contact.secondaryTitle})`;
    }
    return contact.name;
  };

  return (
    <>
      <FormField
        control={form.control}
        name="provider"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="font-bold">Contact</FormLabel>
            <FormControl>
              <div className="relative">
                <GroupedCombobox
                  key={phoneNumber || 'no-phone'}
                  searchKeys={['name', 'type', 'phone', 'secondaryTitle']}
                  label=""
                  groups={[
                    ...(categorizedProviders.suggested.length > 0
                      ? [
                          {
                            label: 'Suggested Matter Contacts',
                            options: categorizedProviders.suggested,
                          },
                        ]
                      : []),
                    ...(categorizedProviders.allOthers.length > 0
                      ? [
                          {
                            label: 'All Matter Contacts',
                            options: categorizedProviders.allOthers,
                          },
                        ]
                      : []),
                    {
                      label: 'Insurance Carriers',
                      options:
                        categorizedProviders.validInsuranceCarrierContacts,
                    },
                  ]}
                  value={field.value}
                  onChange={(value) => {
                    if (value) {
                      field.onChange(value);
                      form.setValue('phoneNumber', value.phone);
                    }
                  }}
                  placeholder={(() => {
                    if (!allowCustomPhoneNumber) {
                      return 'Select a contact';
                    }
                    if (phoneNumber) {
                      return `Phone: ${phoneNumber}`;
                    }
                    return 'Select a contact or enter a number';
                  })()}
                  displayValue={getContactDisplayValue}
                  renderItem={({
                    item: provider,
                    displayValue,
                    isSelected,
                  }) => (
                    <div
                      className={`flex flex-col min-w-0 py-2 px-1 rounded-sm ${isSelected ? 'bg-gray-100' : ''}`}
                    >
                      <div className="font-semibold text-sm text-gray-900 truncate">
                        {provider.name}
                      </div>
                      <div className="flex flex-col text-xs gap-0.5 mt-0.5">
                        {provider.secondaryTitle && (
                          <span className="text-gray-500">
                            {provider.secondaryTitle}
                          </span>
                        )}
                        {provider.phone && (
                          <span className="text-gray-700 font-medium">
                            {formatPhoneNumber(provider.phone)}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  renderSelection={({ item: provider }) =>
                    field.value ? (
                      <ContactSelectionDisplay
                        provider={provider}
                        displayValue={provider.name}
                        isCustomMode={isCustomMode}
                        phoneNumber={phoneNumber}
                        onClear={(e) => {
                          e.stopPropagation();
                          field.onChange(null);
                          form.setValue('phoneNumber', '');
                        }}
                      />
                    ) : (
                      <span className="text-gray-500">
                        {(() => {
                          if (!allowCustomPhoneNumber) {
                            return 'Select a contact';
                          }
                          if (phoneNumber) {
                            return `Phone: ${phoneNumber}`;
                          }
                          return 'Select a contact or enter a number';
                        })()}
                      </span>
                    )
                  }
                />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {allowCustomPhoneNumber && (
        <FormField
          control={form.control}
          name="phoneNumber"
          render={({ field }) => (
            <FormItem className="max-w-[50%]">
              <FormLabel className="font-bold" htmlFor="phone">
                Phone Number
              </FormLabel>
              <FormControl>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="Enter phone number (e.g. 303-217-0796 or +12025550123)"
                  className="h-9 transition-colors data-[invalid=true]:border-red-500/50 data-[invalid=true]:ring-red-500/25"
                  data-invalid={!isPhoneNumberValid(field.value)}
                  {...field}
                />
              </FormControl>
              <FormMessage>
                {(() => {
                  if (!field.value) {
                    return 'Phone number is required';
                  }
                  if (!isPhoneNumberValid(field.value)) {
                    return 'Please enter a valid phone number (e.g. 303-217-0796 or +12025550123)';
                  }
                  return '';
                })()}
              </FormMessage>
            </FormItem>
          )}
        />
      )}
    </>
  );
};
